import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner';
import { Button, Container, Spinner } from 'react-bootstrap'
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { DailySchemaType, DailySchema } from '../../../services/agent-report/schema/DailySchema';
import { zodResolver } from '@hookform/resolvers/zod';
import PropertySelectComponent from './agent/PropertySelectComponent';
import RoomSelectComponent from './agent/RoomSelectComponent';
import YearSelectComponent from './agent/YearSelectComponent';
import DateRangePickerComponent from './agent/DateRangePickerComponentV2';
import useDaily from '../../../services/agent-report/hooks/useDaily';
import SkeletonTable from './agent/SkeletonTable';
import AgentTableReport from './agent/AgentTableReport';
import BasedOnComponent from './agent/BasedOnComponent';
import useMonth from '../../../services/agent-report/hooks/useMonth';
import AgentTableReportYear from './agent/AgentTableReportYear';

const AgentReportComponent: React.FC = () => {
    const { control, handleSubmit, getValues, formState: { errors }, setValue, watch } = useForm<DailySchemaType>({
        resolver: zodResolver(DailySchema),
        defaultValues: {
            filter: {
                property_id: "",
                base_on: "created_reservation",
                start_date: "",
                end_date: "",
                room_type_id: "",
            },
        },
    });

    // State untuk year (terpisah dari useForm karena tidak ada di schema)
    const [year, setYear] = useState<number | null>(null);

    // State untuk menyimpan filter yang akan digunakan saat fetching
    const [filter, setFilter] = useState<DailySchemaType["filter"]>({
        property_id: "",
        base_on: "created_reservation",
        start_date: "",
        end_date: "",
        room_type_id: "",
    });
    
    const queryClient = useQueryClient();

    // Track applied params per report type for refresh logic
    const [appliedMonthlyParams, setAppliedMonthlyParams] = useState<{
        property_id: string;
        room_type_id: string;
        base_on: string;
        year: number;
    } | null>(null);

    const [appliedDailyParams, setAppliedDailyParams] = useState<{
        property_id: string;
        room_type_id: string;
        base_on: string;
        start_date: string;
        end_date: string;
    } | null>(null);
    const [reportType, setReportType] = useState<"monthly" | "daily" | null>(null);

    const { data: agentMonth, isFetching: isFetchingAgentMonth } = useMonth({
        filter: filter,
        type: reportType,
    });

    const { data: agentDaily, isFetching: isFetchingAgentDaily } = useDaily({
        filter: filter,
        type: reportType,
    });

    // Deteksi perubahan form untuk menentukan apakah harus menampilkan tombol refresh atau show
    const currentFormValues = watch();

    const isMonthlyFormChanged = appliedMonthlyParams
        ? currentFormValues.filter?.property_id !== appliedMonthlyParams.property_id ||
          currentFormValues.filter?.room_type_id !== appliedMonthlyParams.room_type_id ||
          currentFormValues.filter?.base_on !== appliedMonthlyParams.base_on ||
          year !== appliedMonthlyParams.year
        : false;

    const isDailyFormChanged = appliedDailyParams
        ? currentFormValues.filter?.property_id !== appliedDailyParams.property_id ||
          currentFormValues.filter?.room_type_id !== appliedDailyParams.room_type_id ||
          currentFormValues.filter?.base_on !== appliedDailyParams.base_on ||
          currentFormValues.filter?.start_date !== appliedDailyParams.start_date ||
          currentFormValues.filter?.end_date !== appliedDailyParams.end_date
        : false;

    // Flags untuk mengecek kesiapan data
    const isReadyToShowMonthly = !isFetchingAgentMonth && agentMonth && reportType === 'monthly';
    const isReadyToShowDaily = !isFetchingAgentDaily && agentDaily && reportType === 'daily';

    // Flags untuk menentukan apakah button harus menampilkan UI refresh
    const showRefreshMonthly = isReadyToShowMonthly && appliedMonthlyParams !== null && !isMonthlyFormChanged;
    const showRefreshDaily = isReadyToShowDaily && appliedDailyParams !== null && !isDailyFormChanged;

    // Fungsi tunggal untuk menangani submit berdasarkan tombol yang ditekan
    const onSubmit = useCallback(
        (data: DailySchemaType, type: "monthly" | "daily") => {
            console.log('data', data);

            let updatedFilter = { ...data.filter };

            if (type === "monthly") {
                if (!year) {
                    console.error("Year must be selected!");
                    return;
                }
                updatedFilter.start_date = `${year}-01-01`;
                updatedFilter.end_date = `${year}-12-31`;

                // If refreshing, invalidate React Query cache
                if (showRefreshMonthly) {
                    queryClient.invalidateQueries({
                        queryKey: ['report', 'month', 'agent']
                    });
                }

                setAppliedMonthlyParams({
                    property_id: data.filter.property_id,
                    room_type_id: data.filter.room_type_id || "",
                    base_on: data.filter.base_on,
                    year: year,
                });
            }

            if (type === "daily") {
                // If refreshing, invalidate React Query cache
                if (showRefreshDaily) {
                    queryClient.invalidateQueries({
                        queryKey: ['report', 'daily', 'agent', 'day']
                    });
                }

                setAppliedDailyParams({
                    property_id: data.filter.property_id,
                    room_type_id: data.filter.room_type_id || "",
                    base_on: data.filter.base_on,
                    start_date: data.filter.start_date,
                    end_date: data.filter.end_date,
                });
            }
            
            setReportType(type);
            setFilter(updatedFilter);
        },
        [year, showRefreshMonthly, showRefreshDaily, queryClient] // Dependensi diupdate
    );

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetchingAgentDaily && agentDaily && reportType === 'daily') {
            toast.success('Agent daily report loaded successfully.');
        }
    }, [isFetchingAgentDaily, agentDaily, reportType]);

    useEffect(() => {
        if (!isFetchingAgentMonth && agentMonth && reportType === 'monthly') {
            toast.success('Agent monthly report loaded successfully.');
        }
    }, [isFetchingAgentMonth, agentMonth, reportType]);

    return (
        <Container fluid className="dashboard-content">
            <form onSubmit={handleSubmit((data) => onSubmit(data, "daily"))}>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                    {/* Kiri: Property, Room, BasedOn */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="w-100">
                            <PropertySelectComponent setValue={setValue} />
                        </div>

                        <div className="w-100">
                            <RoomSelectComponent control={control} errors={errors} setValue={setValue} />
                        </div>

                        <div className="w-100">
                            <BasedOnComponent control={control} errors={errors} setValue={setValue} />
                        </div>
                    </div>

                    {/* Kanan: Year, DateRange + Buttons */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="d-flex flex-column gap-2 w-100">
                            <YearSelectComponent onChange={setYear} />
                            <Button
                                variant={showRefreshMonthly ? "info" : "warning"}
                                type="button"
                                onClick={() => onSubmit(getValues(), "monthly")}
                                disabled={isFetchingAgentMonth}
                            >
                                {isFetchingAgentMonth ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefreshMonthly ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Report</>
                                ) : (
                                    'Show Report'
                                )}
                            </Button>
                        </div>

                        <div className="d-flex flex-column gap-2 w-100">
                            <DateRangePickerComponent control={control} errors={errors} setValue={setValue} />
                            <Button 
                                variant={showRefreshDaily ? "info" : "warning"} 
                                type="submit" 
                                disabled={isFetchingAgentDaily || isFetchingAgentMonth}
                            >
                                {(isFetchingAgentDaily || isFetchingAgentMonth) ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefreshDaily ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Agent Daily Report</>
                                ) : (
                                    'Show Agent Daily Report'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
            {isFetchingAgentMonth && <SkeletonTable columns={14} rows={10} />}
            {isFetchingAgentDaily && <SkeletonTable columns={7} rows={10} />}
            {!isFetchingAgentDaily && agentDaily && reportType === 'daily' && (
                <>
                    <AgentTableReport data={agentDaily} filter={filter} />
                </>
            )}
            {!isFetchingAgentMonth && agentMonth && reportType === 'monthly' && (
                <>
                    <AgentTableReportYear data={agentMonth} startDate={filter.start_date} endDate={filter.end_date} />
                </>
            )}
        </Container>
    )
}

export default AgentReportComponent